// React and Hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// API and Types
import { getTehnicalInspectionAPI } from "services/api/calculations/tehnicalinspection/api";
import { initialTehnicalInspectionDetailForm } from "services/api/calculations/tehnicalinspection/consts";
import { TehnicalInspectionDetailFormValues } from "services/api/calculations/tehnicalinspection/types";
import { Errors } from "services/forms/validation/types";
import {
  TehnicalInspectionDetailPageCmp,
  TehnicalInspectionDetailPageProps,
} from "./types";

// UI Components
import { Modal } from "ui/components/Modal";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { Button } from "ui/components/Button/Button";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";

// Breadcrumbs & Navigation
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { transformRoute, useTranslate } from "helpers";
import { Routes } from "ui/modules/calculation/navigation";

// Page-specific Components
import TehnicalInspectionDetailCard from "./components/TehnicalInspectionDetailCard/TehnicalInspectionDetailCard";
import TehnicalInspectionDeleteCard from "./components/TehnicalInspectionDeleteCard/TehnicalInspectionDeleteCard";

// Constants
import { CALCULATION_TEHNICAL_INSPECTION_DETAIL_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { SIDEBAR_ITEM_CALCULATION_TEHNICAL_INSPECTION } from "app/components/Sidebar/consts";

const TehnicalInspectionDetailPage: TehnicalInspectionDetailPageCmp = (
  props: TehnicalInspectionDetailPageProps
) => {
  const { tehnicalInspectionId } = useParams();

  const { t } = useTranslate();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [formValues, setFormValues] =
    useState<TehnicalInspectionDetailFormValues>(
      initialTehnicalInspectionDetailForm
    );

  const navigate = useNavigate();

  const title = CALCULATION_TEHNICAL_INSPECTION_DETAIL_PAGE_TITLE;

  useEffect(() => {
    if (tehnicalInspectionId) fetchTehnicalInspection(tehnicalInspectionId);
  }, [tehnicalInspectionId]);

  const fetchTehnicalInspection = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialTehnicalInspectionDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getTehnicalInspectionAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_CALCULATION_TEHNICAL_INSPECTION.text,
      route: SIDEBAR_ITEM_CALCULATION_TEHNICAL_INSPECTION.route,
    },
  ];

  const handleDeleteSuccess = useCallback(() => {
    navigate(
      transformRoute(Routes.CalculationTehnicalInspectionSearchPageRouter, {
        tehnicalInspectionId,
      })
    );
  }, []);

  if (!tehnicalInspectionId) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white border-b border-slate-200 rounded-t-md">
          <PageHeader>
            <PageTitle
              title={`${t(title.text)} [${formValues.vehicleGroupName}]`}
              icon={title.icon}
              iconColor={title.iconColor}
            />
            <div className="flex justify-between">
              <Breadcrumb items={breadcrumbs} />
              <div className="flex gap-2">
                <Button
                  size="large"
                  variant="remove"
                  children={t(
                    "admin.calculation.setting.tehnicalinspection.detail.delete"
                  )}
                  onClick={() => setShowDeleteModal(true)}
                  startIcon="/media/icons/trash.svg"
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-4 py-6  lg:px-4">
          <TehnicalInspectionDetailCard
            tehnicalInspectionId={tehnicalInspectionId}
          />
        </div>
      </div>
      {showDeleteModal && (
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <ModalHeader
            title={t(
              "admin.calculation.setting.tehnicalinspection.delete.title"
            )}
            onHide={() => setShowDeleteModal(false)}
          />
          <ModalBody>
            <TehnicalInspectionDeleteCard
              tehnicalInspectionId={tehnicalInspectionId}
              onSuccess={handleDeleteSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

TehnicalInspectionDetailPage.route =
  Routes.CalculationTehnicalInspectionDetailPageRouter;

export default TehnicalInspectionDetailPage;
